import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Tag, Tooltip, Button } from 'antd';
import { CaretDownOutlined, CaretRightOutlined, LockOutlined } from '@ant-design/icons';
import { getFabCount, buildFabricationColumns, selectGridFabrications } from '../../sales/proforma/proformaHelpers';
import {
  pieceStateLabel, pieceKindLabel, pieceIndexLabel
} from './workOrderHelpers';

// ════════════════════════════════════════════════════════════════════
// Stage WO-LINES-GRID — read-only, proforma-styled Lines grid (VIRTUALIZED).
//
// Replaces the plain "Lines & routes" table on the Work Order detail page with
// a dense grid mirroring the proforma line grid's LOOK (# / TYPE / ITEM DESC /
// ACT (MM) / QTY / THICKNESS / WEIGHT / ACT AREA / FABRICATION / RFD) — WITHOUT
// any money columns and fully READ-ONLY.
//
// SCALE: the grid VIRTUALIZES (row windowing) so it stays smooth at thousands of
// lines/pieces. Lines and their (expanded) pieces are flattened into one uniform
// ROW_H stream; only the rows intersecting the viewport are mounted, so "Expand
// all" on a large work order never mounts every piece at once.
//
// Data source: ONLY what the Work Order snapshot carries (spec_snapshot + the
// fabrication_details joined from the source proforma line). Area & weight are
// DERIVED from the actual dims with the exact formulas the printed Work Order
// copy uses (woCopyReport.js) — computed ONCE per line, not per cell.
// ════════════════════════════════════════════════════════════════════

const ROW_H = 30;            // every visual row (line / route / piece-header / piece)
const HEADER_H = 46;         // group band (22) + leaf band (24)
const VBUFFER = 8;           // rows rendered above/below the viewport
const CHILD_INDENT = 28;     // nested (piece) rows indent under their line

const KG_PER_M2_PER_MM = 2.5;               // matches woCopyReport GLASS_KG_PER_M2_PER_MM
const SQFT_PER_SQM = 10.764;        // matches woCopyReport totalSqft factor

// Area — CLAUDE.md 5A: exactly 3 dp, Indian grouping, never rounded to whole.
const NF_AREA = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const NF_WEIGHT = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function num(v) { const n = Number(v); return Number.isFinite(n) ? n : 0; }
function fmtDimMm(v) {
  const n = num(v);
  if (!n) return '';
  const s = n.toFixed(2);
  return s.indexOf('.') === -1 ? s : s.replace(/0+$/, '').replace(/\.$/, '');
}
function fmtThk(v) {
  const n = num(v);
  if (!n) return '';
  const s = n.toFixed(2);
  return s.indexOf('.') === -1 ? s : s.replace(/0+$/, '').replace(/\.$/, '');
}

const GRID_FONT = "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,'Noto Sans',sans-serif";
const GRID_CSS = `
.wolg-wrap{font-family:${GRID_FONT};color:#0f172a;}
.wolg-scroll{position:relative;overflow:auto;max-height:60vh;border:1px solid #cbd5e1;background:#fff;border-radius:6px;box-shadow:0 1px 2px rgba(15,23,42,.04);}
.wolg-scroll::-webkit-scrollbar{width:11px;height:11px;}
.wolg-scroll::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:6px;border:3px solid #fff;}
.wolg-scroll::-webkit-scrollbar-thumb:hover{background:#94a3b8;}
.wolg-grid{position:relative;}
.wolg-header{position:sticky;top:0;z-index:20;display:flex;}
.wolg-hg{display:flex;flex-direction:column;flex:0 0 auto;box-sizing:border-box;}
.wolg-hgrow{display:flex;}
.wolg-hsolo,.wolg-hgtop,.wolg-hleaf{display:flex;flex:0 0 auto;align-items:center;justify-content:center;box-sizing:border-box;
  white-space:nowrap;overflow:hidden;text-transform:uppercase;color:#475569;border-right:1px solid #dbe2ea;border-bottom:1px solid #cbd5e1;}
.wolg-hsolo{width:100%;height:46px;background:#eef2f7;font-size:10px;font-weight:700;letter-spacing:.05em;}
.wolg-hgtop{width:100%;height:22px;background:#eef2f7;font-size:10px;font-weight:700;letter-spacing:.05em;}
.wolg-hleaf{height:24px;background:#f6f8fb;font-size:10px;font-weight:600;letter-spacing:.03em;border-right:1px solid #e6ebf1;}
.wolg-hsolo.fab,.wolg-hgtop.fab,.wolg-hleaf.fab{background:#fff5e9;}
.wolg-hsolo.ar,.wolg-hgtop.ar,.wolg-hleaf.ar{background:#ecfdf3;}
.wolg-vbody{position:relative;}
.wolg-vabs{position:absolute;left:0;display:flex;box-sizing:border-box;height:${ROW_H}px;}
.wolg-row{background:#fff;border-bottom:1px solid #eef1f5;cursor:pointer;}
.wolg-row:hover{background:#f5f8fc;}
.wolg-row.open{background:#eef5ff;}
.wolg-cell{position:relative;flex:0 0 auto;box-sizing:border-box;height:100%;border-right:1px solid #eef1f5;overflow:hidden;display:flex;align-items:center;padding:0 8px;font-size:12px;}
.wolg-cell.ar{background:#f5fdf8;}
.wolg-cell.fab{background:#fffbf4;}
.wolg-cell.right{justify-content:flex-end;text-align:right;font-variant-numeric:tabular-nums;}
.wolg-cell.center{justify-content:center;text-align:center;font-variant-numeric:tabular-nums;}
.wolg-cell .clip{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;width:100%;}
.wolg-serial{display:flex;align-items:center;gap:4px;width:100%;color:#64748b;font-weight:600;font-variant-numeric:tabular-nums;}
.wolg-caret{display:inline-flex;align-items:center;justify-content:center;width:16px;height:16px;border:none;background:transparent;cursor:pointer;color:#64748b;border-radius:3px;font-size:11px;}
.wolg-caret:hover{background:#e2e8f0;color:#1e293b;}
.wolg-type{font-weight:600;color:#1f2733;}
.wolg-fabv{color:#9333ea;font-weight:600;}
/* Nested (child) rows — dense cells matching the parent row, with a blue rail +
   indent so they read as belonging to the line above. */
.wolg-child{background:#fff;border-left:3px solid #93b4f5;border-bottom:1px solid #eef1f5;padding-left:${CHILD_INDENT - 3}px;}
.wolg-child.chead{background:#eef2f7;}
.wolg-child:hover{background:#f5f8fc;}
.wolg-chcell{flex:0 0 auto;box-sizing:border-box;height:100%;border-right:1px solid #eef1f5;overflow:hidden;display:flex;align-items:center;padding:0 8px;font-size:12px;}
.wolg-chcell.center{justify-content:center;text-align:center;font-variant-numeric:tabular-nums;}
.wolg-chcell.hdr{background:#eef2f7;color:#475569;font-size:10px;font-weight:700;letter-spacing:.03em;text-transform:uppercase;}
.wolg-route{background:#fff;border-left:3px solid #93b4f5;align-items:center;gap:8px;padding-left:${CHILD_INDENT - 3}px;overflow-x:auto;overflow-y:hidden;white-space:nowrap;}
.wolg-route .lbl{font-size:11px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.04em;flex:0 0 auto;}
.wolg-route .wolg-routetext{font-size:12px;font-weight:600;color:#334155;white-space:nowrap;}
.wolg-route .rsep{color:#cbd5e1;font-weight:400;}
.wolg-pempty{background:#fff;border-left:3px solid #93b4f5;align-items:center;padding-left:${CHILD_INDENT}px;color:#94a3b8;font-size:12px;}
.wolg-empty{padding:18px;text-align:center;color:#94a3b8;font-size:12px;}
`;

// The ordered route as plain slash-separated text (STAGE / STAGE / STAGE …);
// the lock-milestone stage carries a lock glyph.
function RouteTags({ route }) {
  if (!Array.isArray(route) || !route.length) return <span style={{ color: '#94a3b8' }}>—</span>;
  return (
    <span className="wolg-routetext">
      {route.map((st, i) => (
        <Tooltip key={`${st.departmentCode || st.stage}-${i}`}
          title={st.departmentName || st.departmentCode || st.stage}>
          <span className="rseg">
            {i > 0 ? <span className="rsep"> / </span> : null}
            {st.isLockMilestone ? <LockOutlined style={{ fontSize: 10, marginRight: 3, color: '#64748b' }} /> : null}
            {st.abbreviation || st.departmentCode || st.stage}
          </span>
        </Tooltip>
      ))}
    </span>
  );
}

// Piece production_state → tinted cell background + text colour (replaces the
// coloured pill; the whole State cell now carries the colour).
function pieceStateCellStyle(state) {
  switch (state) {
    case 'IN_ROUTE': return { background: '#e6f0ff', color: '#1d4ed8' };
    case 'COMPLETED': return { background: '#dcfce7', color: '#15803d' };
    case 'SCRAPPED': return { background: '#fee2e2', color: '#b91c1c' };
    case 'NOT_STARTED':
    default: return { background: '#eef1f5', color: '#64748b' };
  }
}

// Build the read-only column set. Fabrication columns are the master fabrications
// that actually appear on at least one line (kept in the master's own order); a
// document with no fabrications drops the whole group. `showRfd` appends the
// per-line READY-FOR-DISPATCH column when the RFD stage is in the route.
function buildColumns(fabColumns, showRfd) {
  const cols = [
    { key: 'sno', group: '#', header: '#', width: 58, kind: 'serial' },
    { key: 'type', group: 'TYPE', header: 'TYPE', width: 92, kind: 'type' },
    { key: 'itemDesc', group: 'ITEM DESC', header: 'ITEM DESC', width: 300, kind: 'item' },
    { key: 'actW', group: 'ACT (MM)', header: 'W', width: 72, kind: 'dim', field: 'widthMm', align: 'right' },
    { key: 'actH', group: 'ACT (MM)', header: 'H', width: 72, kind: 'dim', field: 'heightMm', align: 'right' },
    { key: 'qty', group: 'QTY', header: 'QTY', width: 58, kind: 'qty', align: 'center' },
    { key: 'thickness', group: 'THICKNESS', header: 'THICKNESS', width: 84, kind: 'thk', align: 'right' },
    { key: 'weight', group: 'WEIGHT', header: 'KG', width: 82, kind: 'weight', align: 'right' },
    { key: 'actSqft', group: 'ACT AREA', header: 'SQFT', width: 84, kind: 'sqft', align: 'right', bg: 'ar' },
    { key: 'actSqmt', group: 'ACT AREA', header: 'SQMT', width: 84, kind: 'sqmt', align: 'right', bg: 'ar' }
  ];
  (fabColumns || []).forEach((f) => cols.push({
    key: `fab_${f.code}`, group: 'FABRICATION', header: f.label || f.code,
    width: 54, kind: 'fab', code: f.code, align: 'center', bg: 'fab'
  }));
  // Dispatch tail columns: DISPATCHED (placeholder — wired later) then the
  // READY-FOR-DISPATCH per-line progress (pieces done/routed), rightmost.
  if (showRfd) {
    cols.push({ key: 'dispatched', group: 'DISPATCHED', header: 'PCS', width: 100, kind: 'dispatched', align: 'center' });
    cols.push({ key: 'rfd', group: 'RFD', header: 'RFD', width: 72, kind: 'rfd', align: 'center' });
  }
  return cols;
}

// Consecutive columns sharing a group label collapse into one header cell.
// `solo` = a single-column group whose label equals its column header (e.g.
// #, TYPE, ITEM DESC, QTY, THICKNESS, RFD) → rendered as ONE merged, vertically
// centred header cell instead of the same label stacked on two rows.
function buildGroups(cols) {
  const out = [];
  cols.forEach((c) => {
    const last = out[out.length - 1];
    if (last && last.label === c.group) { last.width += c.width; last.bg = last.bg || c.bg; last.cols.push(c); }
    else out.push({ label: c.group, width: c.width, bg: c.bg, cols: [c] });
  });
  out.forEach((g) => { g.solo = g.cols.length === 1 && String(g.cols[0].header) === String(g.label); });
  return out;
}

export default function WorkOrderLineGrid({ lines, pieces, fabLookups, lineRfd }) {
  const [open, setOpen] = useState(() => new Set());
  const scrollRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportH, setViewportH] = useState(600);

  const lineList = Array.isArray(lines) ? lines : [];
  const pieceList = Array.isArray(pieces) ? pieces : [];
  const rfdByLine = lineRfd && typeof lineRfd === 'object' ? lineRfd : null;
  const showRfd = useMemo(
    () => !!rfdByLine && Object.values(rfdByLine).some((v) => v && (Number(v.routed) || 0) > 0),
    [rfdByLine]
  );

  // Master fabrications → {code, label, rate}, then keep only those present on a
  // line (count > 0). Labels come from the master abbreviation (never hardcoded).
  const fabColumns = useMemo(() => {
    const all = buildFabricationColumns(selectGridFabrications(fabLookups), 100);
    const present = new Set();
    lineList.forEach((l) => {
      const entries = l && l.fabricationDetails && Array.isArray(l.fabricationDetails.entries)
        ? l.fabricationDetails.entries : [];
      entries.forEach((e) => { if (e && num(e.count) > 0 && e.code) present.add(e.code); });
    });
    return all.filter((c) => present.has(c.code));
  }, [fabLookups, lineList]);

  const columns = useMemo(() => buildColumns(fabColumns, showRfd), [fabColumns, showRfd]);
  const groups = useMemo(() => buildGroups(columns), [columns]);
  const lineWidth = useMemo(() => columns.reduce((s, c) => s + c.width, 0), [columns]);

  // Pieces grouped by their line, so each line's child rows list only its own.
  const piecesByLine = useMemo(() => {
    const m = new Map();
    pieceList.forEach((p) => {
      const arr = m.get(p.lineId) || [];
      arr.push(p);
      m.set(p.lineId, arr);
    });
    return m;
  }, [pieceList]);

  // Per-line meta (line no + thickness + component count) for the piece child grid.
  const lineMetaById = useMemo(() => {
    const m = new Map();
    lineList.forEach((l) => {
      const s = l.specSnapshot || {};
      m.set(l.id, {
        lineNo: l.lineNo,
        thicknessMm: s.thicknessMm != null ? Number(s.thicknessMm) : null,
        componentCount: l.componentCount != null ? Number(l.componentCount) : null
      });
    });
    return m;
  }, [lineList]);

  const unitIndexById = useMemo(() => {
    const m = new Map();
    pieceList.forEach((p) => { if (p.kind === 'UNIT') m.set(p.id, { index: p.index, total: p.total }); });
    return m;
  }, [pieceList]);

  // Derived area / weight per line, computed ONCE (not per cell). CLAUDE.md 5A dp.
  const lineDerived = useMemo(() => {
    const m = new Map();
    lineList.forEach((l) => {
      const s = l.specSnapshot || {};
      const qty = num(l.requiredQty) || num(s.qty);
      const areaSqm = (num(s.widthMm) * num(s.heightMm) / 1e6) * qty;
      m.set(l.id, {
        qty,
        weightKg: areaSqm * num(s.thicknessMm) * KG_PER_M2_PER_MM,
        sqmt: areaSqm,
        sqft: areaSqm * SQFT_PER_SQM
      });
    });
    return m;
  }, [lineList]);

  // Piece child columns (rendered with dense wolg cells matching the parent row).
  const pieceColumns = useMemo(() => [
    { key: 'barcode', title: 'Barcode', width: 176,
      render: (r) => r.barcode ? <code className="erp-grid__code">{r.barcode}</code> : <span style={{ color: '#94a3b8' }}>—</span> },
    { key: 'line', title: 'Line', width: 56, align: 'center',
      render: (r) => lineMetaById.get(r.lineId)?.lineNo ?? '—' },
    { key: 'kind', title: 'Kind', width: 96, align: 'center',
      render: (r) => <Tag className="erp-grid__tag" style={{ marginInlineEnd: 0 }}>{r.kind === 'COMPONENT' ? 'Pane' : pieceKindLabel(r.kind)}</Tag> },
    { key: 'index', title: 'Piece', width: 108, align: 'center',
      render: (r) => {
        if (r.kind === 'UNIT') return <span>{r.index} of {r.total}</span>;
        if (r.kind === 'COMPONENT') {
          const u = r.parentUnitId ? unitIndexById.get(r.parentUnitId) : null;
          return <span>{u ? `U${u.index} · ` : ''}{r.index}/{r.total}</span>;
        }
        return <span>{pieceIndexLabel(r.index, r.total)}</span>;
      } },
    { key: 'glass', title: 'Glass', width: 176,
      render: (r) => {
        if (r.kind === 'UNIT') {
          const n = lineMetaById.get(r.lineId)?.componentCount;
          return <span style={{ color: '#94a3b8' }}>Assembly{n ? ` · ${n} panes` : ''}</span>;
        }
        const thk = r.paneThicknessMm != null ? r.paneThicknessMm : lineMetaById.get(r.lineId)?.thicknessMm;
        return <span className="clip" title={r.paneItemDesc || ''}>{thk != null ? `${thk} mm` : '—'}{r.paneItemDesc ? ` · ${r.paneItemDesc}` : ''}</span>;
      } },
    { key: 'currentStage', title: 'Current stage', width: 148,
      render: (r) => r.currentStage ? <span className="clip" style={{ fontWeight: 600, color: '#334155' }}>{r.currentStage}</span> : <span style={{ color: '#94a3b8' }}>—</span> },
    { key: 'state', title: 'State', width: 118, align: 'center',
      cellStyle: (r) => pieceStateCellStyle(r.state),
      render: (r) => <span style={{ fontWeight: 700, fontSize: 11, letterSpacing: '.02em' }}>{(pieceStateLabel(r.state) || '').toUpperCase()}</span> },
    { key: 'locked', title: 'Locked', width: 72, align: 'center',
      render: (r) => r.locked ? <Tooltip title="Spec locked at cutting"><LockOutlined style={{ color: '#64748b' }} /></Tooltip> : <span style={{ color: '#94a3b8' }}>—</span> }
  ], [lineMetaById, unitIndexById]);
  const childWidth = useMemo(() => CHILD_INDENT + pieceColumns.reduce((s, c) => s + c.width, 0), [pieceColumns]);

  // Flatten lines + (expanded) pieces into a single uniform-height row stream so
  // the whole grid can be virtualized. Each expanded line contributes a route
  // row, a piece-header row, then one row per piece (or an empty note).
  const visualRows = useMemo(() => {
    const out = [];
    lineList.forEach((line) => {
      out.push({ type: 'line', line, key: line.id });
      if (open.has(line.id)) {
        out.push({ type: 'route', line, key: `${line.id}:route` });
        const ps = piecesByLine.get(line.id) || [];
        out.push({ type: 'phead', line, key: `${line.id}:phead` });
        if (!ps.length) out.push({ type: 'pempty', line, key: `${line.id}:pempty` });
        else ps.forEach((p) => out.push({ type: 'piece', line, piece: p, key: p.id }));
      }
    });
    return out;
  }, [lineList, open, piecesByLine]);

  const containerWidth = Math.max(lineWidth, childWidth);
  const totalH = visualRows.length * ROW_H;

  // Measure the scroll viewport so the window size adapts to the panel height.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => setViewportH(el.clientHeight || 600);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const onScroll = useCallback((e) => setScrollTop(e.currentTarget.scrollTop), []);

  // Visible window (account for the sticky header consuming the top HEADER_H px).
  const effTop = Math.max(0, scrollTop - HEADER_H);
  const startIdx = Math.max(0, Math.floor(effTop / ROW_H) - VBUFFER);
  const endIdx = Math.min(visualRows.length, Math.ceil((effTop + viewportH) / ROW_H) + VBUFFER);

  const toggle = useCallback((id) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const expandAll = useCallback(() => setOpen(new Set(lineList.map((l) => l.id))), [lineList]);
  const collapseAll = useCallback(() => setOpen(new Set()), []);

  function renderLineCell(line, col) {
    const snap = line.specSnapshot || {};
    const d = lineDerived.get(line.id) || { qty: 0, weightKg: 0, sqmt: 0, sqft: 0 };
    switch (col.kind) {
      case 'serial': {
        const has = (piecesByLine.get(line.id) || []).length > 0;
        const isOpen = open.has(line.id);
        return (
          <div className="wolg-serial">
            {has ? (
              <button type="button" className="wolg-caret" aria-label={isOpen ? 'Collapse' : 'Expand'}
                onClick={(e) => { e.stopPropagation(); toggle(line.id); }}>
                {isOpen ? <CaretDownOutlined /> : <CaretRightOutlined />}
              </button>
            ) : <span style={{ width: 16 }} />}
            <span>{line.lineNo}</span>
          </div>
        );
      }
      case 'type':
        return <span className="wolg-type clip">{snap.lineType || snap.glassTypeCode || '—'}</span>;
      case 'item':
        return <span className="clip" title={snap.itemDesc || snap.glassTypeCode || ''}>{snap.itemDesc || snap.glassTypeCode || '—'}</span>;
      case 'dim':
        return fmtDimMm(snap[col.field]);
      case 'qty':
        return d.qty ? String(d.qty) : '';
      case 'thk':
        return fmtThk(snap.thicknessMm);
      case 'weight':
        return d.weightKg ? NF_WEIGHT.format(d.weightKg) : '';
      case 'sqmt':
        return d.sqmt ? NF_AREA.format(d.sqmt) : '';
      case 'sqft':
        return d.sqft ? NF_AREA.format(d.sqft) : '';
      case 'fab': {
        const c = getFabCount(line, col.code);
        return c > 0 ? <span className="wolg-fabv">{c}</span> : '';
      }
      case 'dispatched':
        // Placeholder until dispatch data is wired into the WO detail payload.
        return <span style={{ color: '#cbd5e1' }}>—</span>;
      case 'rfd': {
        const r = (rfdByLine && rfdByLine[line.id]) || { done: 0, routed: 0 };
        const routed = Number(r.routed) || 0;
        if (!routed) return <span style={{ color: '#cbd5e1' }}>—</span>;
        const done = Number(r.done) || 0;
        const full = done >= routed;
        return <span style={{ fontWeight: 600, color: full ? '#16a34a' : '#334155' }}>{done}/{routed}</span>;
      }
      default:
        return '';
    }
  }

  function renderVisualRow(vr, idx) {
    const top = idx * ROW_H;
    if (vr.type === 'line') {
      const line = vr.line;
      const isOpen = open.has(line.id);
      const hasPieces = (piecesByLine.get(line.id) || []).length > 0;
      return (
        <div key={vr.key} className={`wolg-vabs wolg-row${isOpen ? ' open' : ''}`}
          style={{ top, width: lineWidth }}
          onClick={() => { if (hasPieces) toggle(line.id); }}>
          {columns.map((col) => {
            const cls = ['wolg-cell'];
            if (col.bg) cls.push(col.bg);
            if (col.align === 'right') cls.push('right');
            if (col.align === 'center') cls.push('center');
            return (
              <div key={col.key} className={cls.join(' ')} style={{ width: col.width }}>
                {renderLineCell(line, col)}
              </div>
            );
          })}
        </div>
      );
    }
    if (vr.type === 'route') {
      return (
        <div key={vr.key} className="wolg-vabs wolg-route" style={{ top, width: containerWidth }}>
          <span className="lbl">Route</span>
          <RouteTags route={vr.line.route} />
        </div>
      );
    }
    if (vr.type === 'phead') {
      return (
        <div key={vr.key} className="wolg-vabs wolg-child chead" style={{ top, width: childWidth }}>
          {pieceColumns.map((c) => {
            const cls = ['wolg-chcell', 'hdr'];
            if (c.align === 'center') cls.push('center');
            return <div key={c.key} className={cls.join(' ')} style={{ width: c.width }}>{c.title}</div>;
          })}
        </div>
      );
    }
    if (vr.type === 'pempty') {
      return <div key={vr.key} className="wolg-vabs wolg-pempty" style={{ top, width: containerWidth }}>No pieces</div>;
    }
    // piece row
    return (
      <div key={vr.key} className="wolg-vabs wolg-child" style={{ top, width: childWidth }}>
        {pieceColumns.map((c) => {
          const cls = ['wolg-chcell'];
          if (c.align === 'center') cls.push('center');
          const st = { width: c.width, ...(c.cellStyle ? c.cellStyle(vr.piece) : null) };
          return <div key={c.key} className={cls.join(' ')} style={st}>{c.render(vr.piece)}</div>;
        })}
      </div>
    );
  }

  return (
    <div className="wolg-wrap">
      <style>{GRID_CSS}</style>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 6 }}>
        <Button size="small" onClick={expandAll} disabled={!lineList.length}>Expand all</Button>
        <Button size="small" onClick={collapseAll} disabled={!open.size}>Collapse all</Button>
      </div>
      <div className="wolg-scroll" ref={scrollRef} onScroll={onScroll}>
        <div className="wolg-grid" style={{ minWidth: containerWidth }}>
          <div className="wolg-header">
            {groups.map((g, i) => (
              <div key={`g-${i}`} className="wolg-hg" style={{ width: g.width }}>
                {g.solo ? (
                  <div className={`wolg-hsolo${g.bg ? ` ${g.bg}` : ''}`}>{g.label}</div>
                ) : (
                  <>
                    <div className={`wolg-hgtop${g.bg ? ` ${g.bg}` : ''}`}>{g.label}</div>
                    <div className="wolg-hgrow">
                      {g.cols.map((c) => (
                        <div key={c.key} className={`wolg-hleaf${c.bg ? ` ${c.bg}` : ''}`} style={{ width: c.width }}>{c.header}</div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
          {!lineList.length ? (
            <div className="wolg-empty">No lines</div>
          ) : (
            <div className="wolg-vbody" style={{ height: totalH, minWidth: containerWidth }}>
              {visualRows.slice(startIdx, endIdx).map((vr, i) => renderVisualRow(vr, startIdx + i))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
